import { createApp } from 'vue';
import { createPinia } from 'pinia';
import router from './router';
import App from './App.vue';
import { useEnvironmentStore } from './stores/environment';

// PrimeVue imports
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import PrimeDataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';

// PrimeVue styles
import 'primeicons/primeicons.css';

// Global theme styles
import './styles/shared.css';

// Wrap initialization in try-catch to show errors
try {
  // Validate required environment variables
  const userPoolId = import.meta.env.VITE_USER_POOL_ID;
  const userPoolClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;

  if (!userPoolId) {
    throw new Error('VITE_USER_POOL_ID environment variable is required');
  }
  if (!userPoolClientId) {
    throw new Error('VITE_USER_POOL_CLIENT_ID environment variable is required');
  }

  // Create Pinia store
  const pinia = createPinia();

  // Create and mount Vue app
  const app = createApp(App);
  app.use(pinia);
  app.use(router);

  // Configure PrimeVue with Aura theme
  app.use(PrimeVue, {
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: false
      }
    }
  });

  // Register commonly used PrimeVue components globally
  app.component('Button', Button);
  app.component('InputText', InputText);
  app.component('PrimeDataTable', PrimeDataTable);
  app.component('Column', Column);
  app.component('Dialog', Dialog);

  // Initialize environment store and configure Amplify
  // This must happen after pinia is installed
  const environmentStore = useEnvironmentStore();
  // The store automatically loads from localStorage and configures Amplify
  // We need to trigger the initial Amplify configuration
  environmentStore.switchEnvironment(environmentStore.currentEnvironment);

  app.mount('#app');
} catch (error) {
  // Display error on page if app fails to initialize
  console.error('Application initialization failed:', error);
  const errorMessage = error instanceof Error ? error.message : String(error);
  // Sanitize error message to prevent XSS
  const sanitized = document.createElement('div');
  sanitized.textContent = errorMessage;
  const errorDiv = document.createElement('div');
  errorDiv.innerHTML = `
    <div style="padding: 20px; font-family: sans-serif;">
      <h1 style="color: #d32f2f;">Application Initialization Error</h1>
      <p style="color: #666;">The application failed to start. Please check the console for details.</p>
      <pre style="background: #f5f5f5; padding: 15px; border-radius: 4px; overflow: auto;"></pre>
      <p style="color: #666; margin-top: 20px;">Common issues:</p>
      <ul style="color: #666;">
        <li>Missing environment variables (check .env file)</li>
        <li>Invalid Amplify configuration</li>
        <li>Network connectivity issues</li>
      </ul>
    </div>
  `;
  errorDiv.querySelector('pre')!.appendChild(sanitized.cloneNode(true));
  document.body.appendChild(errorDiv);
}